import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";

import { can } from "@devengo/shared";

import { Badge, Button, Card, ErrorBox, Field, Input, PageTitle } from "../components/ui";
import { api } from "../lib/api";
import { useCurrentUser } from "../lib/session";
import { MailHistory } from "./mail-history";

export interface Contact {
  id: string;
  companyId: string;
  name: string;
  emails: string[];
  phones: string[];
  note: string | null;
  isDefault: boolean;
}

const list = (text: string) =>
  text
    .split(/[,;\n]/)
    .map((v) => v.trim())
    .filter(Boolean);

/** Contacts of the client company and the documents sent to it (spec 015 Stories 1 and 2). */
export function CompanyDataPage({ companyId }: { companyId: string }) {
  const user = useCurrentUser();
  const canEdit = can(user.role, "manageCompanyContacts");
  const [editing, setEditing] = useState<Contact | "new" | null>(null);
  const contacts = useQuery({
    queryKey: ["contacts", companyId],
    queryFn: () => api.get<Contact[]>(`/api/companies/${companyId}/contacts`),
  });
  return (
    <>
      <PageTitle
        subtitle="A quién le escribe el estudio en esta empresa, y lo que se le mandó por mail."
        actions={
          canEdit && editing === null ? (
            <Button onClick={() => setEditing("new")}>Agregar contacto</Button>
          ) : null
        }
      >
        Datos de contacto
      </PageTitle>
      <ErrorBox error={contacts.error} />
      {editing ? (
        <ContactForm
          companyId={companyId}
          contact={editing === "new" ? null : editing}
          onDone={() => setEditing(null)}
        />
      ) : null}
      <div className="mb-4 grid grid-cols-1 gap-3 md:grid-cols-2">
        {contacts.data?.map((c) => (
          <ContactCard key={c.id} contact={c} canEdit={canEdit} onEdit={() => setEditing(c)} />
        ))}
        {contacts.data?.length === 0 ? (
          <p className="text-sm text-brand-muted">
            Todavía no hay contactos. Cargá al menos uno con mail para poder mandarle documentos.
          </p>
        ) : null}
      </div>
      <MailHistory companyId={companyId} />
    </>
  );
}

function ContactCard({
  contact: c,
  canEdit,
  onEdit,
}: {
  contact: Contact;
  canEdit: boolean;
  onEdit: () => void;
}) {
  const client = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const remove = useMutation({
    mutationFn: () => api.delete(`/api/companies/${c.companyId}/contacts/${c.id}`),
    onSuccess: () => client.invalidateQueries({ queryKey: ["contacts", c.companyId] }),
  });
  return (
    <Card
      title={
        <span className="flex items-center gap-2">
          {c.name} {c.isDefault ? <Badge tone="brand">Recibe los documentos</Badge> : null}
        </span>
      }
      actions={
        canEdit ? (
          confirming ? (
            <>
              <Button variant="danger" onClick={() => remove.mutate()} disabled={remove.isPending}>
                Borrar
              </Button>
              <Button variant="secondary" onClick={() => setConfirming(false)}>
                No
              </Button>
            </>
          ) : (
            <>
              <Button variant="ghost" onClick={onEdit}>
                Editar
              </Button>
              <Button variant="ghost" onClick={() => setConfirming(true)}>
                Borrar
              </Button>
            </>
          )
        ) : null
      }
    >
      <dl className="grid grid-cols-[6rem_1fr] gap-y-1 text-sm">
        <dt className="text-brand-muted">Mail</dt>
        <dd>{c.emails.length ? c.emails.join(", ") : "—"}</dd>
        <dt className="text-brand-muted">Teléfono</dt>
        <dd>{c.phones.length ? c.phones.join(", ") : "—"}</dd>
        {c.note ? (
          <>
            <dt className="text-brand-muted">Nota</dt>
            <dd>{c.note}</dd>
          </>
        ) : null}
      </dl>
      <ErrorBox error={remove.error} />
    </Card>
  );
}

function ContactForm({
  companyId,
  contact,
  onDone,
}: {
  companyId: string;
  contact: Contact | null;
  onDone: () => void;
}) {
  const client = useQueryClient();
  const [key] = useState(() => crypto.randomUUID());
  const [name, setName] = useState(contact?.name ?? "");
  const [emails, setEmails] = useState(contact?.emails.join(", ") ?? "");
  const [phones, setPhones] = useState(contact?.phones.join(", ") ?? "");
  const [note, setNote] = useState(contact?.note ?? "");
  const [isDefault, setIsDefault] = useState(contact?.isDefault ?? false);
  const save = useMutation({
    mutationFn: () => {
      const body = { name, emails: list(emails), phones: list(phones), note, isDefault };
      return contact
        ? api.patch(`/api/companies/${companyId}/contacts/${contact.id}`, body)
        : api.postOnce(`/api/companies/${companyId}/contacts`, body, key);
    },
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ["contacts", companyId] });
      onDone();
    },
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    save.mutate();
  };
  return (
    <Card title={contact ? "Editar contacto" : "Nuevo contacto"} className="mb-4">
      <form onSubmit={submit} className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <Field label="Nombre o rol" hint="Ej.: Pablo, dueño · Administración">
          <Input value={name} onChange={(e) => setName(e.target.value)} required />
        </Field>
        <Field label="Mails" hint="Separados por coma.">
          <Input value={emails} onChange={(e) => setEmails(e.target.value)} />
        </Field>
        <Field label="Teléfonos" hint="Separados por coma.">
          <Input value={phones} onChange={(e) => setPhones(e.target.value)} />
        </Field>
        <Field label="Nota">
          <Input value={note} onChange={(e) => setNote(e.target.value)} />
        </Field>
        <label className="flex items-center gap-2 text-sm md:col-span-2">
          <input
            type="checkbox"
            checked={isDefault}
            onChange={(e) => setIsDefault(e.target.checked)}
          />
          Es quien recibe los documentos que manda el estudio
        </label>
        <div className="flex gap-2 md:col-span-2">
          <Button type="submit" disabled={save.isPending}>
            Guardar
          </Button>
          <Button variant="secondary" onClick={onDone}>
            Cancelar
          </Button>
        </div>
      </form>
      <ErrorBox error={save.error} />
    </Card>
  );
}
